import { useEffect } from 'react'
import { useOmniAuth } from '@omni/auth'
import { surfaceForPathname } from '../surfaces'

// Page-open recorder (Sam, 2026-10-18): one row in the tenant's ops.page_view
// each time a signed-in person opens a page, so we can see which tabs each
// customer's staff actually use. Writes go through public.record_page_view
// (EDNA-Command-Center scripts/tenant/page-view.sql, on every tenant), which fills in
// who from the session itself. Only the path (never the query string) and the
// tab id are sent.
//
// Fire-and-forget: never throws, never blocks render, draws nothing, logs
// nothing. Where the tenant has no record_page_view yet, the first refusal
// switches the recorder off for the rest of the visit.

const LOCATION_EVENT = 'omni-shell:location'
const SETTLE_MS = 800
const SKIP_PREFIXES = ['/auth/']

let historyPatched = false
// Surfaces mount their own routers and move with history.pushState directly,
// which fires no event. Patch once so in-surface navigation is seen too.
function patchHistory() {
  if (historyPatched || typeof window === 'undefined') return
  historyPatched = true
  for (const key of ['pushState', 'replaceState'] as const) {
    const original = window.history[key]
    window.history[key] = function patched(this: History, ...args: Parameters<History['pushState']>) {
      const result = original.apply(this, args)
      try {
        window.dispatchEvent(new Event(LOCATION_EVENT))
      } catch {
        // never let the recorder break navigation
      }
      return result
    }
  }
}

let disabled = false
let lastSent = ''

export function PageViewRecorder() {
  const { getClient } = useOmniAuth()

  useEffect(() => {
    if (disabled) return
    patchHistory()
    let timer: ReturnType<typeof setTimeout> | undefined

    const send = () => {
      if (disabled) return
      const path = window.location.pathname
      if (path === lastSent || SKIP_PREFIXES.some((p) => path.startsWith(p))) return
      lastSent = path
      const module = surfaceForPathname(path)?.meta.id ?? null
      try {
        void getClient('crm')
          .schema('public')
          .rpc('record_page_view', { p_path: path, p_module: module })
          .then(
            ({ error, status }) => {
              // Missing function (404 / PGRST202) or refused (401/403): this
              // tenant cannot take page views, stop for the visit.
              if (error && (status === 404 || status === 401 || status === 403 || error.code === 'PGRST202')) {
                disabled = true
              }
            },
            () => {},
          )
      } catch {
        // never throw from the recorder
      }
    }

    // Wait for redirects ('/' -> default tab) to settle, then send the page
    // the person actually landed on. A repeat of the same path is dropped.
    const schedule = () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(send, SETTLE_MS)
    }

    schedule()
    window.addEventListener('popstate', schedule)
    window.addEventListener('omni-shell:navigate', schedule)
    window.addEventListener(LOCATION_EVENT, schedule)
    return () => {
      if (timer) clearTimeout(timer)
      window.removeEventListener('popstate', schedule)
      window.removeEventListener('omni-shell:navigate', schedule)
      window.removeEventListener(LOCATION_EVENT, schedule)
    }
  }, [getClient])

  return null
}
